import { Check, Plus } from "@/components/ui/icons";
import { Pressable, View } from "react-native";

import { cn } from "@/lib/ui/cn";
import { useThemeColors } from "@/theme/theme";

/**
 * Returns a control's spoken name with the reason it cannot be used appended, or the bare name
 * when it can (TRO-673).
 *
 * The "+"/✓ button and both stepper arrows share it so they compose the name identically.
 */
export const withReason = (label: string, reason?: string | null) =>
  reason ? `${label}. Unavailable — ${reason}.` : label;

/**
 * The quick-pick "+"/checkmark on each browse item (#341).
 *
 * `onToggle` feeds the Pull Sheet picking system (#342) in `usePullSheet`, which persists a real
 * `pull_sheet` and `pull_sheet_item` on the server. Callers pass `disabled` while that item's
 * mutation is in flight (`usePullSheet.isPickPending`), so a second tap before the first settles
 * does nothing instead of racing it.
 *
 * It mirrors the app's `Checkbox` overlay (`ui/Checkbox.tsx`, `StockGridCard`'s selection overlay)
 * so vendor-app users recognize it, but lives in `components/browse/` because it carries the public
 * browse surface's pick semantics, not a generic checkbox's.
 */
export function PickButton({
  picked,
  onToggle,
  overlay = false,
  disabled = false,
  hint,
  productName,
}: {
  productName?: string;
  picked: boolean;
  onToggle: () => void;
  /** Opaque backing for sitting on top of card art (grid cell); plain otherwise (list row). */
  overlay?: boolean;
  /** True while this item's own pick mutation is in flight, or while picking is locked. */
  disabled?: boolean;
  /**
   * Why this control cannot be used right now (TRO-673), folded into the spoken name as well as
   * `accessibilityHint`.
   *
   * react-native-web 0.21 forwards neither `accessibilityHint` nor an `aria-` equivalent (see its
   * `forwardedProps`/`createDOMProps`), and the public storefront is a web build (ADR-0017).
   * `accessibilityLabel` maps to `aria-label` everywhere, so the reason goes there, and the hint
   * stays for native.
   */
  hint?: string;
}) {
  const c = useThemeColors();
  return (
    <Pressable
      testID="browse-item-pick"
      onPress={onToggle}
      disabled={disabled}
      accessibilityRole="button"
      accessibilityLabel={withReason(
        picked
          ? (productName ? `Remove ${productName} from your list` : "Remove from your list")
          : (productName ? `Add ${productName} to your list` : "Add to your list"),
        hint,
      )}
      accessibilityHint={hint}
      accessibilityState={{ selected: picked, disabled }}
      className={cn("h-[52px] w-[52px] items-center justify-center", disabled && "opacity-50")}
    >
      <View
        className={cn(
          "h-8 w-8 items-center justify-center rounded-full border-2",
          picked
            ? "border-primary bg-primary"
            : overlay
            ? "border-border bg-bg/90"
            : "border-border bg-surface",
        )}
      >
        {picked
          ? <Check size={18} color={c.primaryForeground} strokeWidth={3} />
          : <Plus size={18} color={c.foreground} strokeWidth={3} />}
      </View>
    </Pressable>
  );
}
